import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api, type AdminSubscriptionListItem, type PaymentTransaction } from '@/services/api';
import { Loader2, RefreshCw, Crown, AlertCircle, Receipt } from 'lucide-react';

type SubscriptionsTab = 'subscriptions' | 'transactions';

const TABS: { key: SubscriptionsTab; label: string }[] = [
  { key: 'subscriptions', label: 'Подписки' },
  { key: 'transactions', label: 'Транзакции' },
];

function isSubscriptionsTab(v: string | null): v is SubscriptionsTab {
  return v === 'subscriptions' || v === 'transactions';
}

function formatDate(s: string | null | undefined): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return s;
  }
}

function subStatusVariant(status: string, isActive: boolean): 'default' | 'secondary' | 'destructive' {
  if (isActive) return 'default';
  if (status === 'cancelled') return 'destructive';
  return 'secondary';
}

function txStatusVariant(status: string): 'default' | 'secondary' | 'destructive' {
  if (status === 'success') return 'default';
  if (status === 'failed') return 'destructive';
  return 'secondary';
}

const TX_STATUS_OPTIONS = [
  { value: '', label: 'Все статусы' },
  { value: 'success', label: 'Успешно' },
  { value: 'pending', label: 'Ожидание' },
  { value: 'failed', label: 'Ошибка' },
  { value: 'cancelled', label: 'Отменено' },
];

function SubscriptionsList() {
  const [items, setItems] = useState<AdminSubscriptionListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { subscriptions } = await api.getAllSubscriptions();
      setItems(subscriptions);
    } catch (err) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : null;
      setError(msg || 'Не удалось загрузить список подписок');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const activeCount = items.filter((i) => i.isActive).length;

  return (
    <>
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={load} disabled={isLoading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Обновить
        </Button>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Всего записей: {items.length} · Активных: {activeCount}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-12 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">Подписок ещё нет.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3 font-medium">ID</th>
                    <th className="py-2 pr-3 font-medium">Store ID</th>
                    <th className="py-2 pr-3 font-medium">Plan</th>
                    <th className="py-2 pr-3 font-medium">Статус</th>
                    <th className="py-2 pr-3 font-medium">Активирован</th>
                    <th className="py-2 pr-3 font-medium">Истекает</th>
                    <th className="py-2 pr-3 font-medium">Отменён</th>
                    <th className="py-2 pr-3 font-medium">Source</th>
                    <th className="py-2 pr-3 font-medium">Кем выдан</th>
                    <th className="py-2 pr-3 font-medium text-right">KZT</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((sub) => (
                    <tr key={sub.id} className="border-b hover:bg-muted/30">
                      <td className="py-2 pr-3">{sub.id}</td>
                      <td className="py-2 pr-3 font-mono text-xs">{sub.storeId}</td>
                      <td className="py-2 pr-3">{sub.planCode}</td>
                      <td className="py-2 pr-3">
                        <Badge variant={subStatusVariant(sub.status, sub.isActive)}>
                          {sub.isActive ? 'active' : sub.status}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3 text-xs">{formatDate(sub.activatedAt)}</td>
                      <td className="py-2 pr-3 text-xs">{formatDate(sub.expiresAt)}</td>
                      <td className="py-2 pr-3 text-xs">{formatDate(sub.cancelledAt)}</td>
                      <td className="py-2 pr-3">{sub.source}</td>
                      <td className="py-2 pr-3 text-xs">{sub.grantedBy || '—'}</td>
                      <td className="py-2 pr-3 text-right">{sub.amountKzt.toLocaleString('ru-RU')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function TransactionsList() {
  const [items, setItems] = useState<PaymentTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async (status?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.getPaymentTransactions({ limit: 100, offset: 0, status: status || undefined });
      setItems(result.transactions);
      setTotal(result.total);
    } catch (err) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : null;
      setError(msg || 'Не удалось загрузить транзакции');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(statusFilter || undefined); }, [load, statusFilter]);

  return (
    <>
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <select
          className="border rounded px-2 py-1 text-sm bg-background"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          {TX_STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <Button variant="outline" size="sm" onClick={() => load(statusFilter || undefined)} disabled={isLoading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Обновить
        </Button>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Всего транзакций: {total}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-12 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">Транзакций нет.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3 font-medium">ID</th>
                    <th className="py-2 pr-3 font-medium">Store ID</th>
                    <th className="py-2 pr-3 font-medium">Plan</th>
                    <th className="py-2 pr-3 font-medium text-right">KZT</th>
                    <th className="py-2 pr-3 font-medium">Статус</th>
                    <th className="py-2 pr-3 font-medium">Wooppay TX</th>
                    <th className="py-2 pr-3 font-medium">Ошибка</th>
                    <th className="py-2 pr-3 font-medium">Инициатор</th>
                    <th className="py-2 pr-3 font-medium">Создана</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((tx) => (
                    <tr key={tx.id} className="border-b hover:bg-muted/30">
                      <td className="py-2 pr-3">{tx.id}</td>
                      <td className="py-2 pr-3 font-mono text-xs">{tx.storeId}</td>
                      <td className="py-2 pr-3">{tx.planCode || '—'}</td>
                      <td className="py-2 pr-3 text-right">{tx.amountKzt.toLocaleString('ru-RU')}</td>
                      <td className="py-2 pr-3">
                        <Badge
                          variant={txStatusVariant(tx.status)}
                          className={tx.status === 'pending' ? 'animate-pulse' : ''}
                        >
                          {tx.status}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs">{tx.wooppayTransactionId || '—'}</td>
                      <td className="py-2 pr-3 text-xs text-destructive max-w-xs truncate">
                        {tx.errorMessage || '—'}
                      </td>
                      <td className="py-2 pr-3 text-xs">{tx.initiatedBy || '—'}</td>
                      <td className="py-2 pr-3 text-xs">{formatDate(tx.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}

export function Subscriptions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const activeTab: SubscriptionsTab = isSubscriptionsTab(rawTab) ? rawTab : 'subscriptions';

  function switchTab(tab: SubscriptionsTab) {
    setSearchParams({ tab }, { replace: true });
  }

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-2xl font-semibold flex items-center gap-2">
        <Crown className="w-6 h-6" />
        Подписки
      </h1>

      <div className="flex border-b gap-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`tab-${t.key}`}
            role="tab"
            aria-selected={activeTab === t.key}
            onClick={() => switchTab(t.key)}
            className={[
              'flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === t.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            ].join(' ')}
          >
            {t.key === 'transactions' && <Receipt className="w-4 h-4" />}
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'subscriptions' && <SubscriptionsList />}
      {activeTab === 'transactions' && <TransactionsList />}
    </div>
  );
}
